import {
  InternalPermissionActions,
  PermissionAction,
  SRN_PARTS_DIVIDER,
} from './srnParser'

export interface RequiredPermissionParams {
  module: string
  /**
   * Used for generation of both srn and permission.
   *
   * If permissionResourceType is defined we use resourceType for generation of srn and permissionResourceType for permission generation.
   */
  resourceType: string
  /**
   * Don't provide resourceId if you want to verify system permission (not content permission).
   * Don't provide resourceId if you check VIEW permission and want to check whether any resource with VIEW permission exists in the module
   * Provide MATCH_ALL_RESOURCES_SIGN if you want to verify permission for specific srn containing MATCH_ALL_RESOURCES_SIGN (e.g. srn:v1:documents:profile:*)
   */
  resourceId?: string | number
  /**
   * Used for cases when resource for srn and permission is not the same
   * E.g. if you ask for log permissions (srn contains log) but it may have permission for entry (permission contains entry)
   */
  permissionResourceType?: string
  action: PermissionAction | InternalPermissionActions
}

export const SRN_VERSION = 'v1'
export const LOGGING_SRN_MODULE = 'logging'
export const TRACKING_SRN_MODULE = 'tracking'
export const CLINICS_SRN_MODULE = 'clinics'
export const SETTINGS_SRN_MODULE = 'settings'
export const POPULATE_SRN_MODULE = 'populate'
export const DOCUMENTS_SRN_MODULE = 'documents'

const CLINIC_SRN_RESOURCE = 'clinic'

/**
 * We need to specify allowed value for srns to prevent system modules like settings from generation of own srn
 * - it would cause server error
 *
 * TODO: should we add CLINICS_SRN_MODULE?
 */
const ALLOWED_MODULES_FOR_SRN = [
  LOGGING_SRN_MODULE,
  TRACKING_SRN_MODULE,
  DOCUMENTS_SRN_MODULE,
]

export const MATCH_ALL_RESOURCES_SIGN = '*'

export const generateSrn = (
  clinicId: number,
  {
    module,
    resourceType,
    resourceId,
  }: Omit<RequiredPermissionParams, 'action'>,
) => {
  return [
    'srn',
    SRN_VERSION,
    clinicId,
    module,
    ...(resourceType ? [resourceType] : []),
    ...(resourceId ? [resourceId] : []),
  ].join(SRN_PARTS_DIVIDER)
}

export const generateBaseClinicSrn = (clinicId: number) =>
  generateSrn(clinicId, {
    module: CLINICS_SRN_MODULE,
    resourceType: CLINIC_SRN_RESOURCE,
    resourceId: clinicId,
  })

export const generateUniqueRequestedSrns = (
  clinicId: number,
  generalSrn: string,
  askedForPermissions: RequiredPermissionParams[],
) =>
  Array.from(
    new Set([
      generalSrn,
      ...askedForPermissions
        .filter((permission) =>
          ALLOWED_MODULES_FOR_SRN.includes(permission.module),
        )
        .map((permissionParams) =>
          generateSrn(clinicId, {
            module: permissionParams.module,
            resourceType: MATCH_ALL_RESOURCES_SIGN,
          }),
        ),
    ]),
  )
